import { useState } from 'react';
import Yup from '~/util/yup';
import { FormModal } from '~/shared/forms/FormModal';
import {
	addLabsOptions,
	getLabDefinitionQuickAddsOptions,
	LabCollectionTechniqueOptions,
	type FullOrderViewModel,
	type LabCommandModel,
	type LabDefinitionViewModel
} from '@generated';
import type { FormHelpers } from '~/shared/forms/types';
import { SubmitButton } from '~/shared/components/buttons/SubmitButton';
import { FormSubmitButton } from '~/shared/forms/buttons/FormSubmitButton';
import { Button } from '~/shared/components/buttons/Button';
import { ChevronLeft, X } from 'lucide-react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { QuickAddNew } from '~/shared/QuickAdd';
import { AutocompleteSearchFormField } from '~/shared/forms/controls/fields/AutocompleteSearchFormField';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { LabDefinitionSearchField } from '~/libraries/labs/LabDefinitionSearchField';
import { getInvalidateOrderQueryKeys } from '~/features/orders/util';
import { AddLabFrequenciesFormControl } from './AddLabFrequenciesFormControl';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';

interface LabCommandModelExtended extends LabCommandModel {
	labDefinitionName: string;
	requiresTechniqueSelection: boolean;
	requiresSource: boolean;
}

const validationSchema = Yup.object().shape({
	indication: Yup.string().label('Indication'),
	icdDiagnosisId: Yup.string()
		.label('ICD Diagnosis')
		.when('indication', {
			is: (indication: string) => !indication || indication.trim().length === 0,
			then: schema => schema.required('ICD Diagnosis is required when text field is blank'),
			otherwise: schema => schema.nullable()
		}),
	//assignedToId: Yup.string().label('Assigned To').nullable(),
	prn: Yup.boolean().label('PRN'),
	labs: Yup.array()
		.label('Labs')
		.min(1, 'At least one lab must be selected')
		.of(
			Yup.object().shape({
				labDefinitionId: Yup.string().label('Lab Definition').required(),
				requiresTechniqueSelection: Yup.boolean(),
				labCollectionTechniqueId: Yup.string()
					.label('Lab Collection Technique')
					.when('requiresTechniqueSelection', {
						is: true,
						then: schema => schema.required('Lab Collection Technique is required'),
						otherwise: schema => schema.nullable()
					}),
				requiresSource: Yup.boolean(),
				source: Yup.string()
					.label('Source')
					.when('requiresSource', {
						is: true,
						then: schema => schema.required(),
						otherwise: schema => schema.nullable()
					})
			})
		),
	labFrequencies: Yup.array()
		.label('Lab Frequencies')
		.of(
			Yup.object().shape({
				scheduledDateAndTime: Yup.date().label('Scheduled Date and Time').required(),
				frequencyCadenceTypeId: Yup.number().label('Frequency Cadence Type').required(),
				assignedToId: Yup.string().label('Assigned To').nullable(),
				notes: Yup.string().label('Notes').nullable()
			})
		)
});

const addLab = (definition: LabDefinitionViewModel, formHelpers: FormHelpers) => {
	const newLab: LabCommandModelExtended = {
		id: window.crypto.randomUUID(),
		labDefinitionId: definition.id,
		labDefinitionName: definition.name,
		requiresSource: definition.requiresSource,
		requiresTechniqueSelection: definition.requiresTechniqueSelection,
		prn: false
	};
	formHelpers.setFieldValue('labs', [...(formHelpers.values.labs as LabCommandModelExtended[]), newLab]);
};

const removeLab = (id: string, formHelpers: FormHelpers) => {
	formHelpers.setFieldValue(
		'labs',
		(formHelpers.values.labs as LabCommandModelExtended[]).filter(lab => lab.id !== id)
	);
};

export function AddLabsModal({ fullOrder, closeModal }: { fullOrder: FullOrderViewModel; closeModal: () => void }) {
	const { data } = useSuspenseQuery(getLabDefinitionQuickAddsOptions());
	const [currentView, setCurrentView] = useState<'list' | 'details'>('list');

	const handleNext = (formHelpers: FormHelpers) => {
		if ((formHelpers.values.labs as LabCommandModelExtended[]).length === 0) {
			formHelpers.setFieldError('labs', 'At least one lab must be selected');
			return;
		}
		setCurrentView('details');
	};

	const getSubmitButton = (formHelpers: FormHelpers) => {
		if (currentView === 'list') {
			return <SubmitButton onSubmit={() => handleNext(formHelpers)} submitLabel="Next" submittingLabel="Loading..." />;
		}
		return (
			<>
				<Button onClick={() => setCurrentView('list')} Icon={ChevronLeft} label="Previous" />
				<FormSubmitButton />
			</>
		);
	};

	return (
		<FormModal
			title="Add Labs"
			initialValues={{
				orderId: fullOrder.id,
				indication: null,
				icdDiagnosisId: null,
				prn: false,
				labs: [],
				labFrequencies: []
			}}
			saveMutation={{
				...addLabsOptions,
				invalidateQueryKeys: [...getInvalidateOrderQueryKeys(fullOrder)]
			}}
			validationSchema={validationSchema}
			submitButton={getSubmitButton}
			onSuccess={closeModal}
			closeModal={closeModal}
			size="xl"
		>
			{formHelpers => {
				const labs = formHelpers.values.labs as LabCommandModelExtended[];
				if (currentView === 'list') {
					return (
						<div className="flex flex-col gap-8">
							<QuickAddNew options={data} onSelect={val => addLab(val, formHelpers)} />
							<LabDefinitionSearchField onChange={definition => addLab(definition, formHelpers)} />
							<div className="flex flex-col gap-2">
								<h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Selected Labs</h3>
								{labs.length === 0 ? (
									<p className="text-sm text-gray-500">No labs selected yet.</p>
								) : (
									labs.map((lab, index) => (
										<div key={lab.id} className="flex items-center justify-between rounded border px-3 py-2">
											<div className="flex flex-row items-center gap-4">
												<span className="font-medium text-gray-900">{lab.labDefinitionName}</span>
												{lab.requiresTechniqueSelection && (
													<SelectFormField
														hideLabel
														name={`labs[${index}].labCollectionTechniqueId`}
														options={LabCollectionTechniqueOptions}
														placeholder="Lab Collection Technique"
													/>
												)}
												{lab.requiresSource && <TextFormField name={`labs[${index}].source`} label="Source" />}
											</div>

											<Button
												Icon={X}
												appearance="outline"
												intent="destructive"
												onClick={() => removeLab(lab.id, formHelpers)}
												aria-label={`Remove ${lab.labDefinitionName}`}
											/>
										</div>
									))
								)}
							</div>
							<div className="grid grid-cols-3 gap-2">
								<AutocompleteSearchFormField searchType="icd-diagnosis" name="icdDiagnosisId" />
								<TextFormField name="indication" />
							</div>
							<CheckboxFormField name="prn" hideLabel />
						</div>
					);
				}
				return (
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-2">
							<span className="font-medium text-gray-900">Selected Labs</span>
							{labs.map((lab, index) => (
								<Lab key={lab.id} lab={lab} index={index} removeLab={id => removeLab(id, formHelpers)} />
							))}
						</div>
						<AddLabFrequenciesFormControl fullOrder={fullOrder} formHelpers={formHelpers} />
					</div>
				);
			}}
		</FormModal>
	);
}

function Lab({ lab, index, removeLab }: { lab: LabCommandModelExtended; index: number; removeLab: (id: string) => void }) {
	return (
		<div key={lab.id} className="flex items-center justify-between rounded border px-3 py-2">
			<div className="flex flex-row items-center gap-2">
				<span className="mr-2 font-medium text-gray-900">{lab.labDefinitionName}</span>
				{lab.requiresTechniqueSelection && (
					<SelectFormField name={`labs[${index}].labCollectionTechniqueId`} options={LabCollectionTechniqueOptions} />
				)}
				{lab.requiresSource && <TextFormField name={`labs[${index}].source`} label="Source" />}
			</div>

			<Button
				Icon={X}
				appearance="outline"
				intent="destructive"
				onClick={() => removeLab(lab.id)}
				aria-label={`Remove ${lab.labDefinitionName}`}
			/>
		</div>
	);
}
